import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Download, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { Button } from '@/shared/ui/buttons/Button';
import { PartyStatusChip } from '@/shared/ui/chips/PartyStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiPartiesManifestGet, usePartyDetail } from '@/entities/parties';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { downloadBlob, safeFilenamePart } from '@/shared/utils/download';
import {
  FeaturesPartiesAddPackageInline,
  FeaturesPartiesDeleteDialog,
  FeaturesPartiesStatusDialog,
} from '@/features/parties';
import { formatDate, formatDateTime } from '@/shared/utils/date';
import { formatDecimalTrim } from '@/shared/utils/currency';
import type { ApiPackagesGetItem } from '@/shared/types/api/packages';

const formatWeight = (row: ApiPackagesGetItem): string => {
  if (row.weight === null || row.weight === undefined || row.weight === '') return '—';
  const n = typeof row.weight === 'number' ? row.weight : Number(row.weight);
  if (!Number.isFinite(n)) return '—';
  const formatted = formatDecimalTrim(n);
  return `${formatted} ${row.weight_unit}`;
};

export const WidgetsPartyDetailMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const toast = useToast();
  const query = usePartyDetail(id);
  const [statusOpen, setStatusOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const canChangeStatus = usePerm('parties.detail.change_status');
  const canAddParcels = usePerm('parties.detail.add_packages');
  const canDelete = usePerm('parties.delete');
  // Manifest endpoint (`GET /parties/:id/manifest`) is gated on `parties.detail`
  // server-side — mirror that so the button only shows to staff who can call it.
  const canDownloadManifest = usePerm('parties.detail');

  const handleDownloadManifest = async () => {
    if (manifestLoading) return;
    setManifestLoading(true);
    try {
      const blob = await apiPartiesManifestGet(id);
      if (blob.type && !blob.type.includes('pdf')) {
        throw new Error('not-a-pdf');
      }
      const namePart = safeFilenamePart(query.data?.name ?? id, id || 'party');
      downloadBlob(blob, `manifest-${namePart}.pdf`);
    } catch {
      toast.error(t('parties.detail.manifestError'));
    } finally {
      setManifestLoading(false);
    }
  };

  if (query.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <ErrorState
          message={extractErrorMessage(query.error, t('parties.detail.notFound'))}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const party = query.data;
  const packages = party.packages ?? [];
  const showAttachSection = canAddParcels && party.status === 'COLLECTING';

  return (
    <>
      <PageHeader
        title={party.name}
        subtitle={t('parties.detail.subtitle')}
        showBack
        backTo="/parties"
        right={
          canDownloadManifest || canChangeStatus || canDelete ? (
            <div className="flex items-center gap-2">
              {canDownloadManifest && (
                <Button
                  variant="outline"
                  leftIcon={<Download className="h-4 w-4" />}
                  loading={manifestLoading}
                  onClick={() => void handleDownloadManifest()}
                >
                  {manifestLoading
                    ? t('parties.detail.manifestDownloading')
                    : t('parties.detail.manifest')}
                </Button>
              )}
              {canChangeStatus && (
                <Button
                  variant="outline"
                  leftIcon={<ArrowRightLeft className="h-4 w-4" />}
                  onClick={() => setStatusOpen(true)}
                >
                  {t('parties.detail.changeStatus')}
                </Button>
              )}
              {canDelete && party.status === 'COLLECTING' && (
                <Button
                  variant="danger"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('parties.delete.button')}
                </Button>
              )}
            </div>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <Section>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PartyStatusChip status={party.status} />
            <span className="text-sm text-ink-muted">
              {t('parties.detail.createdAt', { date: formatDateTime(party.created_at) })}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-soft">
              {t('parties.detail.packagesCount')} <strong>{party.packages_count}</strong>
            </p>
          </div>
        </Section>

        <Section>
          <div className="mb-3">
            <h3 className="text-base font-semibold text-ink">
              {t('parties.detail.packagesTitle')}
            </h3>
          </div>
          {packages.length === 0 ? (
            <EmptyState
              title={t('parties.detail.packagesEmptyTitle')}
              description={t('parties.detail.packagesEmptyDescription')}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('parties.detail.packagesColumns.name')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('parties.detail.packagesColumns.parcelsCount')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('parties.detail.packagesColumns.weight')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('parties.detail.packagesColumns.createdAt')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr
                      key={pkg.id}
                      className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2.5 font-medium text-ink">
                        <span className="block max-w-[260px] truncate">{pkg.name}</span>
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">{pkg.parcels_count}</td>
                      <td className="px-3 py-2.5 text-ink-soft">{formatWeight(pkg)}</td>
                      <td className="px-3 py-2.5 text-ink-soft">{formatDate(pkg.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {showAttachSection && (
          <Section>
            <FeaturesPartiesAddPackageInline
              partyId={party.id}
              attachedPackages={packages}
            />
          </Section>
        )}
      </div>

      <FeaturesPartiesStatusDialog
        party={statusOpen ? party : null}
        onClose={() => setStatusOpen(false)}
      />
      <FeaturesPartiesDeleteDialog
        party={deleteOpen ? party : null}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
};
